import React from "react";
import {
  SkeletonPulse,
  SkeletonPulseLight,
  /* header */
  HeaderSkeletonWrapper,
  SkeletonLogoBlock,
  SkeletonHeaderRight,
  /* page */
  PageSkeletonWrapper,
  SkeletonHero,
  SkeletonTitleSection,
  SkeletonTwoCol,
  SkeletonImageBox,
  SkeletonTextCol,
  /* footer */
  FooterSkeletonWrapper,
  SkeletonFooterTopRow,
  SkeletonFooterLogo,
  SkeletonFooterNavLinks,
  SkeletonFooterSecondRow,
  SkeletonFooterDivider,
  SkeletonFooterBottomRow,
  SkeletonSocialIcons,
} from "./styles";

/* ─────────────────────────────────────────────
   HEADER
   Mirrors: wide grey logo block on the left,
   hamburger lines + dark avatar circle on the right.
───────────────────────────────────────────── */
export const HeaderSkeleton = () => (
  <HeaderSkeletonWrapper>
    {/* Logo image block — wide grey rectangle flush left */}
    <SkeletonLogoBlock />

    {/* Right side: hamburger + avatar */}
    <SkeletonHeaderRight>
      {/* Hamburger icon (3 short stacked bars) */}
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {[24, 24, 24].map((w, i) => (
          <SkeletonPulse key={i} style={{ width: w, height: 3, borderRadius: 2 }} />
        ))}
      </div>

      {/* Avatar circle */}
      <SkeletonPulse style={{ width: 38, height: 38, borderRadius: "50%", background: "#ccc" }} />
    </SkeletonHeaderRight>
  </HeaderSkeletonWrapper>
);

/* ─────────────────────────────────────────────
   PAGE CONTENT
   Mirrors:
     1. Full-width dark-grey hero banner with centred image icon + title text
     2. Centred heading + subtitle below
     3. Two-column: left image placeholder, right text+list
───────────────────────────────────────────── */
export const PageSkeleton = () => (
  <PageSkeletonWrapper>
    {/* ① Hero banner */}
    <SkeletonHero>
      {/* Broken-image icon placeholder */}
      <SkeletonPulseLight style={{ width: 120, height: 120, borderRadius: 6 }} />
      {/* Title text */}
      <SkeletonPulseLight style={{ width: 340, height: 40, borderRadius: 4 }} />
      {/* Subtitle text */}
      <SkeletonPulseLight style={{ width: 240, height: 16, borderRadius: 4 }} />
    </SkeletonHero>

    {/* ② Centred title section */}
    <SkeletonTitleSection>
      {/* Large heading */}
      <SkeletonPulse style={{ width: 300, height: 52, borderRadius: 4 }} />
      {/* Subtitle */}
      <SkeletonPulse style={{ width: 200, height: 16, borderRadius: 4 }} />
    </SkeletonTitleSection>

    {/* ③ Two-column content row */}
    <SkeletonTwoCol>
      {/* Left: image placeholder */}
      <SkeletonImageBox />

      {/* Right: numbered list + paragraphs */}
      <SkeletonTextCol>
        {/* List item 1 — two lines */}
        <SkeletonPulse style={{ width: "90%", height: 13, borderRadius: 3 }} />
        <SkeletonPulse style={{ width: "70%", height: 13, borderRadius: 3 }} />

        {/* List item 2 — one line */}
        <SkeletonPulse style={{ width: "25%", height: 13, borderRadius: 3, marginTop: 4 }} />

        {/* Paragraph block */}
        {["100%", "95%", "100%", "88%", "100%", "72%"].map((w, i) => (
          <SkeletonPulse
            key={i}
            style={{ width: w, height: 13, borderRadius: 3, marginTop: i === 3 ? 10 : 0 }}
          />
        ))}
      </SkeletonTextCol>
    </SkeletonTwoCol>
  </PageSkeletonWrapper>
);

/* ─────────────────────────────────────────────
   FOOTER
   Mirrors:
     Top row  — small logo icon + text | nav links spread horizontally
     Second row — 4 catalogue link placeholders
     Divider
     Bottom row — copyright text left | 3 social icon circles right
───────────────────────────────────────────── */
export const FooterSkeleton = () => (
  <FooterSkeletonWrapper>
    {/* Top row */}
    <SkeletonFooterTopRow>
      {/* Logo */}
      <SkeletonFooterLogo>
        <SkeletonPulseLight style={{ width: 32, height: 32, borderRadius: "50%" }} />
      </SkeletonFooterLogo>

      {/* Horizontal nav link pills */}
      <SkeletonFooterNavLinks>
        {[72, 52, 58, 90, 68, 80, 72].map((w, i) => (
          <SkeletonPulseLight key={i} style={{ width: w, height: 13, borderRadius: 3 }} />
        ))}
        {/* Sub-items, slightly indented, narrower */}
        {[130, 80].map((w, i) => (
          <SkeletonPulseLight key={`sub${i}`} style={{ width: w, height: 13, borderRadius: 3 }} />
        ))}
        {/* Nested items */}
        {[60, 52, 68, 62].map((w, i) => (
          <SkeletonPulseLight key={`nest${i}`} style={{ width: w, height: 13, borderRadius: 3 }} />
        ))}
      </SkeletonFooterNavLinks>
    </SkeletonFooterTopRow>

    {/* Second nav row (catalogue links) */}
    <SkeletonFooterSecondRow>
      {[90, 100, 100, 100].map((w, i) => (
        <SkeletonPulseLight key={i} style={{ width: w, height: 13, borderRadius: 3 }} />
      ))}
    </SkeletonFooterSecondRow>

    {/* Divider */}
    <SkeletonFooterDivider />

    {/* Bottom row */}
    <SkeletonFooterBottomRow>
      {/* Copyright text block */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <SkeletonPulseLight style={{ width: 200, height: 12, borderRadius: 3 }} />
        <SkeletonPulseLight style={{ width: 120, height: 12, borderRadius: 3 }} />
      </div>

      {/* Social icon circles */}
      <SkeletonSocialIcons>
        {[1, 2, 3].map((i) => (
          <SkeletonPulseLight key={i} style={{ width: 22, height: 22, borderRadius: "50%" }} />
        ))}
      </SkeletonSocialIcons>
    </SkeletonFooterBottomRow>
  </FooterSkeletonWrapper>
);

export { SkeletonPulse };
